import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from '../../components/UI';
import toast from 'react-hot-toast';
import { HiOutlineMail, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import logo from '../../assets/logo.png';

const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!identifier.trim()) {
      setError('Please enter your email or phone number');
      return;
    }

    // Validate identifier - must be valid email OR valid phone
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\d\s\-+]+$/;
    const trimmedIdentifier = identifier.trim();

    if (!emailRegex.test(trimmedIdentifier) && !phoneRegex.test(trimmedIdentifier)) {
      setError('Please enter a valid email address or phone number');
      return;
    }

    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(identifier.trim(), password);

      if (result && result.success) {
        toast.success('Welcome Admin! Redirecting...');
        navigate('/app/', { replace: true });
      } else {
        // Display error from result using both state and toast
        const errorMessage = result?.error || 'Login failed. Please try again.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Login handleSubmit error:', err);
      const errorMsg = 'An unexpected error occurred. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    // Allow both email and phone characters
    setIdentifier(value);
    setError('');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-indigo-700 to-primary-800 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 bg-white/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-64 sm:w-80 md:w-[500px] h-64 sm:h-80 md:h-[500px] bg-indigo-400/20 rounded-full blur-3xl transform translate-x-1/3 translate-y-1/3" />
        <div className="absolute top-1/2 left-1/2 w-40 sm:w-56 md:w-64 h-40 sm:h-56 md:h-64 bg-primary-300/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Content */}
      <div className="relative flex-1 flex flex-col justify-center px-4 py-8 sm:py-12">
        <div className="mx-auto w-full max-w-sm sm:max-w-md">
          {/* Logo */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center lg:w-[300px] lg:h-[80px] w-60 rounded-2xl bg-white backdrop-blur-sm mb-4 shadow-2xl ring-1 ring-white/20 overflow-hidden">
              <img src={logo} alt="TeleValley" className="w-full h-full object-contain p-1.5 sm:p-2" />
            </div>
            {/* <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">TeleValley</h1> */}
            {/* <p className="mt-1.5 sm:mt-2 text-white/70 text-sm font-medium">Unified Login Portal</p> */}
          </div>

          {/* Login Card */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-5 sm:p-8 ring-1 ring-white/20">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2">
              Welcome Back
            </h2>
            <p className="text-center text-gray-500 text-sm mb-6 sm:mb-8">
              Sign in to access your dashboard
            </p>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-300">
                <p className="text-sm text-red-700 text-center font-medium">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              <Input
                label="Email or Phone Number"
                type="text"
                value={identifier}
                onChange={handleIdentifierChange}
                placeholder="Enter your email or phone"
                icon={<HiOutlineMail className="w-5 h-5" />}
                disabled={isLoading}
              />

              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="Enter your password"
                icon={<HiOutlineLockClosed className="w-5 h-5" />}
                disabled={isLoading}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600 p-1 focus:outline-none"
                  >
                    {showPassword ? (
                      <HiOutlineEyeOff className="w-5 h-5" />
                    ) : (
                      <HiOutlineEye className="w-5 h-5" />
                    )}
                  </button>
                }
              />

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-600">Remember me</span>
                </label>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            {/* Info Box */}
            {/* <div className="mt-5 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500 text-center">
                <strong className="text-gray-700">Demo Admin:</strong> admin_telecaller@gmail.com
              </p>
            </div> */}
          </div>

          {/* Bottom Info */}
          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-xs text-white/50">
              © {new Date().getFullYear()} TeleValley. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;